import { QueryKey, useMutation, UseQueryResult } from '@tanstack/react-query'
import { queryClient, QueryOptions, UseMutationResult, useResource, useResources } from '.'
import {
  createTwoParties,
  createThreeParties,
  fetch,
  get,
  PeriodSummary,
  FetchPeriodSummariesParams,
  TwoPartiesPeriodSummary,
  ThreePartiesPeriodSummary,
  SimulatePeriodSummary,
  PeriodSummaryUpdatePayload,
  update,
  simulateThreeParties,
  simulateTwoParties,
  SimulateTwoPartiesPeriodSummary,
} from '../api/period-summaries'
import {
  CreateTwoPartiesParams,
  CreateThreePartiesParams,
} from '../../../backend/src/services/resources/period-summaries/period-summaries.controller.service.types'

// Queries
/**
 * Hook pour récupérer un period summary par son ID
 */
export const usePeriodSummaryQuery = (id?: string): UseQueryResult<PeriodSummary, any> =>
  useResource('period-summaries', get, id)

/**
 * Hook pour récupérer la liste des period summaries avec filtres
 */
export const usePeriodSummariesQuery = (
  filters?: Partial<FetchPeriodSummariesParams>,
  options?: QueryOptions,
): UseQueryResult<PeriodSummary[], any> =>
  useResources<PeriodSummary[], Partial<FetchPeriodSummariesParams>>(
    'period-summaries',
    fetch,
    filters || {},
    options,
  )

export const useSimulateThreePartiesPeriodSummaryQuery = (
  params: CreateThreePartiesParams,
  options?: QueryOptions,
): UseQueryResult<SimulatePeriodSummary, any> =>
  useResources<SimulatePeriodSummary, any>(
    'period-summaries',
    simulateThreeParties,
    params,
    options,
  )

export const useSimulateTwoPartiesPeriodSummaryQuery = (
  params: CreateTwoPartiesParams,
  options?: QueryOptions,
): UseQueryResult<SimulateTwoPartiesPeriodSummary, any> =>
  useResources<SimulateTwoPartiesPeriodSummary, any>(
    'period-summaries',
    simulateTwoParties,
    params,
    options,
  )

// Mutations
/**
 * Hook pour créer un period summary à deux parties
 */
const useCreateTwoPartiesMutationKey = (): QueryKey => ['period-summaries', 'two-parties', 'create']
export const useCreateTwoPartiesMutation = (): UseMutationResult<
  TwoPartiesPeriodSummary,
  CreateTwoPartiesParams,
  any
> =>
  useMutation(createTwoParties, {
    mutationKey: useCreateTwoPartiesMutationKey(),
    onSuccess: () => {
      invalidatePeriodSummariesQueries()
    },
  })

/**
 * Hook pour créer un period summary à trois parties
 */
const useCreateThreePartiesMutationKey = (): QueryKey => [
  'period-summaries',
  'three-parties',
  'create',
]
export const useCreateThreePartiesMutation = (): UseMutationResult<
  ThreePartiesPeriodSummary,
  CreateThreePartiesParams,
  any
> =>
  useMutation(createThreeParties, {
    mutationKey: useCreateThreePartiesMutationKey(),
    onSuccess: () => {
      invalidatePeriodSummariesQueries()
    },
  })

// Methods
/**
 * Invalide toutes les queries liées aux period summaries
 */
export const invalidatePeriodSummariesQueries = (): void => {
  queryClient.invalidateQueries(['period-summaries'])
  queryClient.invalidateQueries(['missions/weeks-summary'])
  queryClient.invalidateQueries(['missions/weeks-summary/status'])
  queryClient.invalidateQueries(['worker-counters'])
}

const useUpdateMutationKey = (): QueryKey => ['period-summaries', 'update']
export const useUpdateMutation = (): UseMutationResult<
  PeriodSummary,
  PeriodSummaryUpdatePayload,
  any
> =>
  useMutation(update, {
    mutationKey: useUpdateMutationKey(),
    onSuccess: () => {
      invalidatePeriodSummariesQueries()
    },
  })
